package com.dotdrives.web

import com.dotdrives.controller.AuthController
import com.dotdrives.controller.AuthUiState
import com.dotdrives.web.ui.*
import kotlinx.browser.window
import react.*
import react.dom.html.ReactHTML.a
import react.dom.html.ReactHTML.div
import react.dom.html.ReactHTML.h1
import react.dom.html.ReactHTML.style
import web.cssom.ClassName

private val inviteUuid =
    Regex("[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}")

fun inviteIdFromLocation(hash: String, search: String = ""): String? {
    if (
        hasRecoveryRouteHint(hash, search) ||
            search.removePrefix("?").split('&').contains("recovery=1") ||
            hasGiverCallback(search)
    )
        return null
    if (hash.startsWith("#/invite/"))
        return hash.removePrefix("#/invite/").takeIf(inviteUuid::matches)
    if (hash.startsWith("#/") && hash != "#/signin") return null
    val candidates = search.removePrefix("?").split('&').filter { it.startsWith("invite=") }
    return candidates.singleOrNull()?.removePrefix("invite=")?.takeIf(inviteUuid::matches)
}

fun inviteCallbackUrl(base: String, invite: String?): String? =
    invite?.takeIf(inviteUuid::matches)?.let { "$base?invite=$it" }

external interface InvitePageProps : Props {
    var inviteId: String
    var auth: AuthController
    var state: AuthUiState
}

val InvitePage =
    FC<InvitePageProps> { props ->
        val workflow =
            useWorkflowController(
                props.state.session!!.userId,
                "invite:${props.inviteId}",
                "invite",
            )
        val state = useFlowState(workflow.state)
        val org = state.result?.get("org_id")?.toString()?.trim('"')
        useEffect(org) {
            if (org != null && inviteIdFromLocation(window.location.hash, window.location.search) == props.inviteId) {
                window.history.replaceState(null, "", "${window.location.pathname}#/dashboard")
                props.auth.refresh()
                window.asDynamic().dispatchEvent(js("new Event('hashchange')"))
            }
        }
        InviteStyles()
        div {
            className = ClassName("invite-route")
            div {
                className = ClassName("invite-card")
                a {
                    href = "${window.location.pathname}#/"
                    ariaLabel = "Dot Drives home"
                    className = ClassName("invite-brand dot-home-link")
                    +"Dot Drives"
                }
                h1 { +"Join your team" }
                div {
                    +"Signed in as ${props.state.session?.email}. This address must match the invitation and be verified."
                }
                WorkflowFeedback {
                    controller = workflow
                    this.state = state
                }
                DotButton {
                    text = "Accept invitation"
                    enabled = !state.busy && state.pending == null
                    onClick = {
                        workflow.perform(
                            "workflows",
                            "invite.accept",
                            mapOf("invite_id" to props.inviteId),
                        )
                    }
                }
                DotButton {
                    text = "Use another account"
                    onClick = props.auth::signOut
                }
                div {
                    +"If the invitation has expired or was revoked, ask an administrator for a new invitation."
                }
            }
        }
    }

private val InviteStyles =
    FC<Props> {
        com.dotdrives.web.pages.settings.SettingsStyles()
        style {
            +"""
        .invite-route { min-height:100vh;display:flex;align-items:center;justify-content:center;padding:32px 20px;background:#F5F7F8;color:#222D32; }
        .invite-card { width:100%;max-width:540px;padding:32px;border:1px solid #E4E8EC;border-radius:20px;background:white;box-shadow:0 10px 35px #222D3210; }
        .invite-brand { font-family:'Quicksand',sans-serif;font-size:20px;font-weight:800;margin-bottom:24px; }
        .invite-card h1 { font-family:'Quicksand',sans-serif;font-size:30px;line-height:1.2;margin:0 0 18px; }
        .invite-card p,.invite-card > div { line-height:1.6;overflow-wrap:anywhere; }
        .invite-card .dot-button { margin:18px 12px 18px 0; }
        .invite-card .dot-banner { margin-top:18px; }
        @media(max-width:600px){.invite-route{align-items:flex-start;padding-top:40px}.invite-card{padding:24px}.invite-card .dot-button{width:100%;margin:10px 0}}
    """
        }
    }

val InvalidInvitationPage =
    FC<Props> {
        InviteStyles()
        div {
            className = ClassName("invite-route")
            div {
                className = ClassName("invite-card")
                a {
                    href = "${window.location.pathname}#/"
                    ariaLabel = "Dot Drives home"
                    className = ClassName("invite-brand dot-home-link")
                    +"Dot Drives"
                }
                h1 { +"Invitation unavailable" }
                div {
                    +"This invitation link is invalid. Ask a team administrator for a new invitation."
                }
                DotButton {
                    text = "Sign in"
                    onClick = { window.location.hash = "#/signin" }
                }
            }
        }
    }
